import React from 'react'
import { Box, ToggleButton, ToggleButtonGroup } from '@mui/material'
import HubIcon from '@mui/icons-material/Hub'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { SlidingMark, useSlidingSelection } from './SlidingSelection'

/**
 * The two ways of looking at the same workflow. Story #150 RN180 keeps its look — two square tabs flush with the
 * bar, no border and no pill, an edge under the active one — and #167 moves them into the "Workflow" bar, so
 * they take no row of their own. A click on them must not fold the section the bar belongs to. No tooltip: the
 * label is already on the button (PO, 28/09/2026).
 */
/**
 * Where the pair sits, the same in Graph and in Tree (#167): on the height of the "Workflow" bar, just left of
 * its fold arrow. The tree's toolbar starts at that height and leaves `SURFACE_SWITCH_SLOT_PX` free on its right.
 */
export const SURFACE_BAR_HEIGHT_PX = 48
export const SURFACE_SWITCH_RIGHT_PX = 56
export const SURFACE_SWITCH_SLOT_PX = 240

const TAB_SX = {
  borderRadius: 0,
  border: 'none',
  textTransform: 'none',
  fontSize: 13,
  lineHeight: 1.4,
  py: 0.25,
  px: 1.25,
  color: 'text.secondary',
  borderBottom: '2px solid transparent',
  position: 'relative',
  zIndex: 1,
  // The underline is one, and it slides from Graph to Tree (`SlidingMark`); the button only changes colour.
  '&.Mui-selected': {
    color: 'primary.main',
    backgroundColor: 'transparent'
  },
  '&.Mui-selected:hover': { backgroundColor: 'action.hover' },
  '& > span': { display: 'inline-flex', alignItems: 'center', gap: 0.75 }
}

const WorkflowSurfaceSwitch = ({ surface, onSurfaceChange }) => {
  const { t_ } = useSmartTranslation()
  const [switchRef, mark] = useSlidingSelection(surface)
  return (
    <Box ref={switchRef} sx={{ position: 'relative', display: 'inline-flex' }}>
      <SlidingMark box={mark} variant='underline' />
      <ToggleButtonGroup
        value={surface}
        exclusive
        size='small'
        data-testid='workflow-surface-switch'
        onClick={(event) => event.stopPropagation()}
        onChange={(event, next) => {
          if (next) {
            onSurfaceChange(next)
          }
        }}
        sx={{ flexShrink: 0, '& .MuiToggleButtonGroup-grouped': { border: 'none', borderRadius: 0 } }}
      >
        <ToggleButton value='graph' data-testid='workflow-surface-graph' aria-label={t_('Graph')} sx={TAB_SX}>
          <span><HubIcon sx={{ fontSize: 16 }} /> {t_('Graph')}</span>
        </ToggleButton>
        <ToggleButton value='tree' data-testid='workflow-surface-tree' aria-label={t_('Tree')} sx={TAB_SX}>
          <span><AccountTreeIcon sx={{ fontSize: 16 }} /> {t_('Tree')}</span>
        </ToggleButton>
      </ToggleButtonGroup>
    </Box>
  )
}

export default WorkflowSurfaceSwitch
